import { useId } from 'react';

import { DARK_LAYER1_GLASS_FILL } from '../../styles/elevation';
import {
  BODY_PANEL_EXPANDED_TOP_EDGE_PATH,
  BODY_PANEL_TOP_EDGE_PATH,
  PANEL_GEOMETRY,
  panelGeometryKey,
} from './bodyPanelPath';

/** What the deck panel's glass sees behind it — the cover image OR the
 *  emoji tile's tint pair. Fuels the SVG fake-frost replica below. */
export interface PanelCover {
  image?: string;
  tint?: { backgroundColor: string; borderColor: string };
}

/** What the FOLDER panel's glass sees behind it: the folder colour plus the
 *  preview-page stack. Positions come from `FOLDER_PAGE_LAYOUTS` — inside
 *  the panel the pages span its full height (their tops sit far above), so
 *  only the horizontal inset matters. */
export interface PanelFolderCover {
  folderColor: string;
  /** Preview pages back→front: horizontal inset (% of panel width) + sheet
   *  fill. `fill` omitted = the plain white/dark sheet. */
  pages: { insetX: number; fill?: string }[];
}

/** Tile box behind the deck panel, in PANEL viewBox coordinates: the tile is
 *  inset 8px inside the card and starts (cardH − panelH − 8) above the panel
 *  top. Web design px (the web responsive scale is always 1). */
const DECK_REPLICA = {
  deck: { tileTop: -83, tileH: 164 },
  deckExpanded: { tileTop: -107, tileH: 209 },
} as const;

/**
 * Web grid body panel — the scooped glass sheet, drawn as ONE inline SVG:
 * the blurred backdrop replica, the translucent surface fill, and the border
 * are all children of the SAME `<path>` in the SAME coordinate space.
 *
 * WHY one SVG and not CSS layers (hard-won — do not "simplify" back):
 * CSS cannot give a vector path a blurred backdrop. `backdrop-filter` only
 * exists on rectangular HTML boxes, so shaping it needs a separate
 * `mask-image` / `clip-path` / wrapper layer — and EVERY such combination
 * goes through Chromium's composited-effects path, which rasterizes the
 * effect layer a few px LARGER than its element whenever the page renders
 * under a scale transform (Storybook canvas zoom, page zoom, DPR changes).
 * The layers visibly detach: glass pokes past the card border. Masks,
 * clip-paths, `isolation`, `will-change`, and live CSS `filter` replicas all
 * failed the same way at some zoom level. SVG-internal effects
 * (`feGaussianBlur`, `<clipPath>`, path fills/strokes) are resolved entirely
 * by the SVG rasterizer at paint time — the compositor only ever sees flat
 * pixels, so shape, frost, fill, and border cannot disagree.
 *
 * The frost is therefore FAKE: DeckCard passes `cover` (its cover image or
 * emoji-tile tint) and the SVG re-draws that content behind the shape and
 * blurs it. Folder cards don't supply a replica yet — they keep a real
 * `backdrop-filter` box inset 8px from every panel edge, so the compositor
 * overshoot lands over the flat folder colour INSIDE the card.
 *
 * The native counterpart (`CardBodyPanel.native.tsx`) keeps its
 * MaskedView + BlurView pipeline — RN has no CSS compositor to fight.
 */
export function CardBodyPanel({
  shape = 'deck',
  size = 'compact',
  cover,
  folderCover,
}: {
  shape?: 'deck' | 'folder';
  /** Grid subvariant — picks the matching path so nothing is stretched. */
  size?: 'compact' | 'expanded';
  /** See the fake-frost note above. Native ignores this. */
  cover?: PanelCover;
  /** Folder-card counterpart of `cover`. Native ignores this. */
  folderCover?: PanelFolderCover;
}) {
  const uid = useId();
  const key = panelGeometryKey(shape, size);
  const replica =
    shape === 'deck' && cover ? DECK_REPLICA[key as 'deck' | 'deckExpanded'] : undefined;
  const { path, viewBox } = PANEL_GEOMETRY[key];
  const [, , vw, vh] = viewBox.split(' ').map(Number);
  // Colours are palette CLASSES, not hex: they follow the theme (and any
  // re-pointed dark ramp) through the CSS variables, which a raw hex cannot.
  //
  // The glass is the card's own SURFACE colour, a little translucent so the
  // cover still glows through the frost — the `fill` twin of the elevation
  // ladder's `DARK_LAYER1_GLASS` (`dark:bg-secondary-50/85`, styles/
  // elevation.ts): same colour as the DeckCard shell, a touch more opaque than
  // light's 70%. It used to be the mirrored `secondary-950` hex — near-black,
  // darker than the shell it sits in.
  const glassFill = `fill-secondary-50/70 ${DARK_LAYER1_GLASS_FILL}`;
  // What the frost replica pretends sits behind the card: its shell colour
  // (`DARK_LAYER1`, `secondary-50`, in dark — so the class is the same in
  // both themes). Opaque — it is overscanned under the blur.
  const shellFill = 'fill-secondary-50';
  // A plain preview page — FolderTile's sheet (`bg-secondary-50`).
  const sheetFill = 'fill-secondary-50';
  // Same token as the card's border overlay (`cardBorderClass`).
  const lineStroke = 'stroke-secondary-200';
  // Deck strokes only its OPEN top edge (the DeckCard border overlay draws
  // the sides/bottom; a second stroke there doubles it). Folder panels have
  // no border overlay, so the whole closed outline is stroked.
  const deckEdge =
    key === 'deckExpanded' ? BODY_PANEL_EXPANDED_TOP_EDGE_PATH : BODY_PANEL_TOP_EDGE_PATH;

  return (
    <span className="absolute inset-0 block" aria-hidden>
      <svg
        className="absolute inset-0"
        width="100%"
        height="100%"
        viewBox={viewBox}
        preserveAspectRatio="none">
        {/* The shape clip serves the replica AND the inner border stroke. */}
        <defs>
          <clipPath id={`${uid}s`}>
            <path d={path} />
          </clipPath>
        </defs>
        {replica && cover ? (
          <>
            <defs>
              <clipPath id={`${uid}t`}>
                <rect
                  x={8}
                  y={replica.tileTop}
                  width={vw - 16}
                  height={replica.tileH}
                  rx={20}
                />
              </clipPath>
              {/* userSpaceOnUse region: the default %-region clips the smear
                  near the shape's top edge (content extends far above it). */}
              <filter
                id={`${uid}f`}
                filterUnits="userSpaceOnUse"
                x={-40}
                y={replica.tileTop - 40}
                width={vw + 80}
                height={vh - replica.tileTop + 80}>
                <feGaussianBlur stdDeviation="12" />
              </filter>
            </defs>
            <g clipPath={`url(#${uid}s)`}>
              <g filter={`url(#${uid}f)`}>
                {/* Flat card-bg stand-in, overscanned past the shape so the
                    blur falloff can never show inside it. */}
                <rect
                  x={-40}
                  y={-40}
                  width={vw + 80}
                  height={vh + 80}
                  className={shellFill}
                />
                {cover.image ? (
                  <image
                    href={cover.image}
                    x={8}
                    y={replica.tileTop}
                    width={vw - 16}
                    height={replica.tileH}
                    preserveAspectRatio="xMidYMin slice"
                    clipPath={`url(#${uid}t)`}
                  />
                ) : cover.tint ? (
                  <rect
                    x={8}
                    y={replica.tileTop}
                    width={vw - 16}
                    height={replica.tileH}
                    rx={20}
                    fill={cover.tint.backgroundColor}
                    stroke={cover.tint.borderColor}
                  />
                ) : null}
              </g>
            </g>
          </>
        ) : folderCover ? (
          <>
            <defs>
              <filter
                id={`${uid}f`}
                filterUnits="userSpaceOnUse"
                x={-52}
                y={-52}
                width={vw + 104}
                height={vh + 104}>
                <feGaussianBlur stdDeviation="12" />
              </filter>
            </defs>
            <g clipPath={`url(#${uid}s)`}>
              <g filter={`url(#${uid}f)`}>
                {/* Page-background stand-in, overscanned past the shape. */}
                <rect
                  x={-40}
                  y={-40}
                  width={vw + 80}
                  height={vh + 80}
                  className={shellFill}
                />
                {/* Folder colour behind the WHOLE panel — mirrors the native
                    under-panel slab (FolderLikeCard): overscanned past the
                    shape so the glass never samples the page background
                    (the clip does the shaping). */}
                <rect
                  x={-40}
                  y={-40}
                  width={vw + 80}
                  height={vh + 80}
                  fill={folderCover.folderColor}
                />
                {/* Preview pages back→front: they span the panel's full
                    height (their tops sit far above the shape, their
                    bottoms reach the card bottom — the native pages are
                    pinned bottom-0 the same way).
                    A PLAIN sheet is left out in dark: there it is the
                    near-black canvas tone, so through the frost it read as
                    a dark band running off both sides of the panel, right
                    under the title and the ⋯ (design review 2026-10, "the
                    fade outs are out of DS"). The glass then shows the
                    folder colour evenly, like the deck panel. A tinted
                    sheet keeps its colour in both themes. */}
                {folderCover.pages.map((pg, i) => (
                  <rect
                    key={i}
                    x={(pg.insetX / 100) * vw}
                    y={-40}
                    width={vw - 2 * (pg.insetX / 100) * vw}
                    height={vh + 80}
                    fill={pg.fill}
                    className={pg.fill ? undefined : `${sheetFill} dark:hidden`}
                  />
                ))}
              </g>
            </g>
          </>
        ) : null}
        {/* The panel surface — the SAME path the replica is clipped by, so
            shape and glass are one drawing and cannot detach. */}
        <path d={path} className={glassFill} />
        {shape === 'folder' ? (
          // INNER 1px rim: a centred stroke loses its outer half wherever
          // the path runs along the svg viewport edge (sides/bottom/flat
          // shoulder), rendering thinner than the top curves. Stroking 2px
          // and clipping to the shape keeps exactly the inner 1px on EVERY
          // edge.
          <path
            d={path}
            fill="none"
            className={lineStroke}
            strokeWidth={2}
            clipPath={`url(#${uid}s)`}
          />
        ) : (
          <path
            d={deckEdge}
            transform="translate(0 0.5)"
            fill="none"
            className={lineStroke}
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
    </span>
  );
}
